import React, { useState, useEffect, useCallback } from 'react';
import { MediaFileRead, MediaUsageResponse } from '../types/api';
import { apiService } from '../services/api';
import { ArrowLeft, Download, Edit2, Save, X, Check, Image, Film, File, Link as LinkIcon, FileText } from 'lucide-react';
import { urlParams } from '../utils/urlParams';
import { useVideoThumbnail } from '../hooks/useVideoThumbnail';
import { formatHorseName } from '../utils/horseName';

interface MediaDetailViewProps {
  mediaId: string;
  onNavigateBack?: () => void;
}

export const MediaDetailView: React.FC<MediaDetailViewProps> = ({ 
  mediaId, 
  onNavigateBack 
}) => {
  const [media, setMedia] = useState<MediaFileRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isEditingFilename, setIsEditingFilename] = useState(false);
  const [editedFilename, setEditedFilename] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [usage, setUsage] = useState<MediaUsageResponse | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  
  const isVideoContent = media?.content_type.startsWith('video/');
  const videoThumbnail = useVideoThumbnail(isVideoContent ? previewUrl : null);

  const loadMediaDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const mediaData = await apiService.getMediaFileInfo(mediaId);
      setMedia(mediaData);
      setEditedNotes(mediaData.notes || '');
      setEditedFilename(mediaData.ui_filename);
    } catch (error) {
      console.error('Error loading media details:', error);
      setError(error instanceof Error ? error.message : 'Failed to load media details');
    } finally {
      setLoading(false);
    }
  }, [mediaId]);

  const loadMediaPreview = useCallback(async () => {
    if (!media) return;
    
    try {
      const blob = await apiService.fetchMediaBlob(media.id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Failed to load media preview:', error);
    }
  }, [media]);

  const loadMediaUsage = useCallback(async () => {
    try {
      setLoadingUsage(true);
      const usageData = await apiService.getMediaUsage(mediaId);
      setUsage(usageData);
    } catch (error) {
      console.error('Failed to load media usage:', error);
      // Don't show error to user - usage is optional info
    } finally {
      setLoadingUsage(false);
    }
  }, [mediaId]);

  useEffect(() => {
    loadMediaDetail();
    loadMediaUsage();
  }, [loadMediaDetail, loadMediaUsage]);

  useEffect(() => {
    if (media && (media.content_type.startsWith('image/') || media.content_type.startsWith('video/'))) {
      loadMediaPreview();
    }
  }, [media, loadMediaPreview]);

  const handleNavigateBack = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      urlParams.navigateBack();
    }
  };

  const handleDownload = () => {
    if (!media) return;
    
    apiService.fetchMediaBlob(media.id)
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = media.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download failed:', error);
        alert('Download failed. Please try again.');
      });
  };

  const handleUpdateNotes = async () => {
    if (isUpdating || !media) return;
    
    setIsUpdating(true);
    try {
      const updatedMedia = await apiService.updateMediaFileNotes(media.id, editedNotes);
      setMedia(updatedMedia);
      setIsEditingNotes(false);
    } catch (error) {
      console.error('Failed to update notes:', error);
      alert('Failed to update notes. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateFilename = async () => {
    if (isUpdating || !media || !editedFilename.trim()) return;
    
    setIsUpdating(true);
    try {
      const updatedMedia = await apiService.updateMediaFileUIFilename(media.id, editedFilename.trim());
      setMedia(updatedMedia);
      setIsEditingFilename(false);
    } catch (error) {
      console.error('Failed to update filename:', error);
      alert('Failed to update filename. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedNotes(media?.notes || '');
    setIsEditingNotes(false);
  };

  const handleCancelFilenameEdit = () => {
    setEditedFilename(media?.ui_filename || '');
    setIsEditingFilename(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleHorseClick = (horseId: string) => {
    urlParams.navigateToHorseDetail(horseId);
  };

  const getFileIcon = () => {
    if (!media) return <File size={48} className="text-gray-600" />;
    
    if (media.content_type.startsWith('image/')) {
      return <Image size={48} className="text-green-600" />;
    }
    if (media.content_type.startsWith('video/')) {
      return <Film size={48} className="text-blue-600" />;
    }
    return <File size={48} className="text-gray-600" />;
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Media</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Media Not Found</h2>
          <p className="text-gray-600 mb-4">The requested media file could not be found.</p>
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isImage = media.content_type.startsWith('image/');
  const isVideo = media.content_type.startsWith('video/');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button and actions */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleNavigateBack}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              <span className="hidden sm:inline">Back to Media</span>
              <span className="sm:hidden">Back</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Download size={16} className="mr-2" />
                <span className="hidden sm:inline">Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Media Display */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {/* Media Preview */}
          <div className="aspect-w-16 aspect-h-9 bg-gray-100">
            {isImage && previewUrl ? (
              <img
                src={previewUrl}
                alt={media.ui_filename}
                className="w-full h-auto object-contain max-h-96 bg-black rounded-t-lg"
              />
            ) : isVideo && videoThumbnail ? (
              <div className="relative bg-black rounded-t-lg">
                <img
                  src={videoThumbnail}
                  alt={media.ui_filename}
                  className="w-full h-auto object-contain max-h-96"
                />
                {/* Video player overlay */}
                <video
                  src={previewUrl || undefined}
                  className="w-full h-auto object-contain max-h-96 absolute inset-0"
                  controls
                  playsInline
                  preload="none"
                  poster={videoThumbnail}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 rounded-t-lg">
                <div className="text-center">
                  {getFileIcon()}
                  <p className="mt-4 text-lg font-medium text-gray-900">{media.ui_filename}</p>
                  {(isImage || isVideo) && !previewUrl && (
                    <div className="mt-4 animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  )}
                  {!isImage && !isVideo && (
                    <p className="mt-2 text-gray-600">This file type cannot be previewed</p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Media Info */}
          <div className="p-6">
            <div className="mb-6">
              {/* Editable Filename */}
              {isEditingFilename ? (
                <div className="mb-4">
                  <input
                    type="text"
                    value={editedFilename}
                    onChange={(e) => setEditedFilename(e.target.value)}
                    placeholder="Enter filename..."
                    className="w-full px-3 py-2 text-xl font-bold border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={255}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateFilename();
                      if (e.key === 'Escape') handleCancelFilenameEdit();
                    }}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      {editedFilename.length}/255 characters
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelFilenameEdit}
                        disabled={isUpdating}
                        className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        <X size={16} className="mr-1" />
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateFilename}
                        disabled={isUpdating || !editedFilename.trim()}
                        className="flex items-center px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check size={16} className="mr-1" />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-2 group flex items-start gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 flex-1">{media.ui_filename}</h1>
                  <button
                    onClick={() => setIsEditingFilename(true)}
                    className="opacity-0 group-hover:opacity-100 flex items-center text-sm text-blue-600 hover:text-blue-800 transition-opacity mt-1"
                  >
                    <Edit2 size={18} className="mr-1" />
                    Edit
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span>{media.content_type}</span>
                <span>•</span>
                <span>{formatFileSize(media.file_size)}</span>
                <span>•</span>
                <span>Uploaded: {formatDate(media.upload_date)}</span>
              </div>
              {media.ui_filename !== media.original_filename && (
                <p className="text-xs text-gray-400 mt-2 text-left" title="Original filename">
                  Original: {media.original_filename}
                </p>
              )}
            </div>

            {/* Notes Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
                {!isEditingNotes && (
                  <button
                    onClick={() => setIsEditingNotes(true)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Edit2 size={16} className="mr-1" />
                    Edit
                  </button>
                )}
              </div>

              {isEditingNotes ? (
                <div className="space-y-3">
                  <textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    placeholder="Add notes about this media file..."
                    className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={6}
                    maxLength={1000}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {editedNotes.length}/1000 characters
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelEdit}
                        disabled={isUpdating}
                        className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        <X size={16} className="mr-1" />
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateNotes}
                        disabled={isUpdating}
                        className="flex items-center px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isUpdating ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                        ) : (
                          <Save size={16} className="mr-1" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-md p-4 min-h-[100px]">
                  {media.notes ? (
                    <p className="text-gray-700 whitespace-pre-wrap">{media.notes}</p>
                  ) : (
                    <p className="text-gray-500 italic">No notes added yet. Click Edit to add notes.</p>
                  )}
                </div>
              )}
            </div>

            {/* Usage Section */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Where This Media is Used</h3>
                {loadingUsage && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
              </div>

              {usage ? (
                <>
                  {/* Usage Summary */}
                  <div className="mb-4 p-4 bg-blue-50 rounded-md">
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center">
                        <FileText size={16} className="text-blue-600 mr-2" />
                        <span className="text-gray-700">
                          <span className="font-semibold text-blue-600">{usage.usage_count}</span>
                          {' '}{usage.usage_count === 1 ? 'note' : 'notes'}
                        </span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center">
                        <LinkIcon size={16} className="text-blue-600 mr-2" />
                        <span className="text-gray-700">
                          <span className="font-semibold text-blue-600">{usage.horses_count}</span>
                          {' '}{usage.horses_count === 1 ? 'horse' : 'horses'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Horses and Notes List */}
                  {usage.horses.length > 0 ? (
                    <div className="space-y-4">
                      {usage.horses.map((horse) => (
                        <div 
                          key={horse.horse_id}
                          className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                        >
                          {/* Horse Name - Clickable */}
                          <button
                            onClick={() => handleHorseClick(horse.horse_id)}
                            className="flex items-center text-left w-full group mb-3"
                          >
                            <LinkIcon size={16} className="text-gray-400 mr-2 group-hover:text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {formatHorseName(horse.horse_name)}
                            </h4>
                            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {horse.notes.length} {horse.notes.length === 1 ? 'note' : 'notes'}
                            </span>
                          </button>

                          {/* Notes List */}
                          <div className="space-y-2 pl-6">
                            {horse.notes.map((note) => (
                              <div 
                                key={note.note_id}
                                className="bg-gray-50 rounded p-3 border border-gray-100"
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <h5 className="font-medium text-gray-900 text-sm">{note.title}</h5>
                                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                    {formatDate(note.created_at)}
                                  </span>
                                </div>
                                {note.content && (
                                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                                    {note.content}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <FileText size={32} className="mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">This media file is not linked to any horses yet.</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Add it to a horse's notes to start using it.
                      </p>
                    </div>
                  )}
                </>
              ) : !loadingUsage ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-600">Unable to load usage information.</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
